import TabPage from "./../factories/tab-page";
import * as ko from "knockout";
import Bao from "./../services/bao-service";
import SubscriptionViewModel from "../factories/subscription-view-model";
import '../components/table-control/table-control'
import baoRouter from "./../services/bao-router";
import BaoMenu from "./../services/bao-menu";
import Guid from "./../factories/guid";

/**
 * WorkItem Collection/TaskBook ViewModel class
 */
class WorkItemCollection extends SubscriptionViewModel {

    //#region Constructors

    /**
     * Constructor
     * @param tabPage - current tab
     */
    public constructor(tabPage: TabPage) {
        super(tabPage);
        this.WorkItemCollectionObservers();
    }

    //#endregion

    //#region Properties

    WorkItemCollectionId: KnockoutObservable<string> = ko.observable();
    /**
     * Key of collection
     */
    WorkItemCollectionKey: KnockoutObservable<string> = ko.observable();

    WorkItemCollections: KnockoutObservableArray<{
        WorkItemCollectionId: string;
        Title: string
    }> = ko.observableArray([]);

    /**
     * Header of number column 
     */
    ColHeaderNumber: KnockoutObservable<string> = ko.observable();
    ColHeaderTitle: KnockoutObservable<string> = ko.observable();
    ColHeaderDescription: KnockoutObservable<string> = ko.observable();
    ColHeaderPriority: KnockoutObservable<string> = ko.observable();
    ColHeaderCategory: KnockoutObservable<string> = ko.observable();
    ColHeaderStartDate: KnockoutObservable<string> = ko.observable();
    ColHeaderEndDate: KnockoutObservable<string> = ko.observable();
    ColHeaderWorkItemTypeName: KnockoutObservable<string> = ko.observable();
    ColHeaderWorkItemStatus: KnockoutObservable<string> = ko.observable();
    TabTitle: KnockoutObservable<string> = ko.observable();
    //Selected workitem from the workitem collection
    WorkItemCollectionSelectedId: KnockoutObservable<string> = ko.observable();

    //Fovourite Properties
    FavouriteCaptionProperty: string = 'Title';
    FavouriteColorCSS: string = 'text-warning';
    FavouriteIconCSS: string = 'fal fa-calendar-check';
    FavouriteSort: number = 20;
    FavouritePrimaryKeyName: string = 'WorkItemCollectionId'

    //New Grid
    AvailableColumns: KnockoutObservableArray<any> = ko.observableArray([]);
    SearchKey = ko.observable<string>('WorkItem');

    /**
      * (Filter) If true then show virtual items (WorkItems) on the list
      */
    ShowVirtual = ko.observable<boolean>(false);

    /**
     * Start date (filter)
     */
    StartDateHtml: KnockoutObservable<string> = ko.observable('');

    /**
     * End date (filter)
     */
    EndDateHtml: KnockoutObservable<string> = ko.observable('');

    /**
    * True if view is loeaded
    */
    IsLoaded: boolean = false;

    /**
     * If true, the date filters are currently being modified
     */
    ChangingData: boolean = false;

    /**
     * List of permissions for each WorkItemCollection
     */
    WorkItemCollectionsPermissions: any;

    //#endregion

    //#region Public Methods

    /**
     * WorkItem Collection Observers
     */
    WorkItemCollectionObservers() {
        this.WorkItemCollectionId.subscribe((value) => {
            this.SetPermissions();
            this.GetAvailableColumns();
            if (value) {
                Bao.raiseEvent("table-control:load", this.SearchKey);
                baoRouter.setHash("workItemcollection/" + this.WorkItemCollectionId());
                this.WorkItemCollectionSelectedId(this.WorkItemCollectionId());
            }
        });

        this.TabPage.UrlData.subscribe((value) => {
            if (value.WorkItemCollectionId && this.WorkItemCollectionId() != value.WorkItemCollectionId && this.WorkItemCollections().find(o => o.WorkItemCollectionId == value.WorkItemCollectionId)) {
                this.WorkItemCollectionSelectedId(value.WorkItemCollectionId)
            }
        });

        this.ShowVirtual.subscribe(() => this.RefreshList());

        this.StartDateHtml.subscribe(() => this.RefreshList());

        this.EndDateHtml.subscribe((newValue) => {
            this.ChangingData = true;
            if (newValue === null) {
                this.ShowVirtual(false);
            }
            this.ChangingData = false;
            this.RefreshList();
        });
    }

    /**
     * Complete event
     * @returns jquery promise
     */
    CompleteCheck = (): boolean | JQueryPromise<boolean> => {
        let completed: boolean = true;
        return completed;
    }

    /**
     * Init event
     */
    init = () => {
        this.loadLabels()
            .then(_ => this.loadWorkItemCollections())
            .then(_ => this.SetPermissions())
            .then(_ => this.IsLoaded = true);
    }
    /**
     * Page loaded
     * */
    Loaded = () => {
        this.RegisterMenuClick();
    }

    /**
     * Load labels
     * @returns JQueryPromise
     */
    loadLabels = (): JQueryPromise<void> => {
        let def = $.Deferred<void>();
        this.TabPage.IsLoading(true);

        $.when(Bao.Session.GetMessage("msgGridColTitle"), Bao.Session.GetMessage("msgGridColDescription"), Bao.Session.GetMessage("msgGridColPriority"),
            Bao.Session.GetMessage("msgGridColCategory"), Bao.Session.GetMessage("msgGridColStartDate"), Bao.Session.GetMessage("msgGridColEndDate"), Bao.Session.GetMessage("msgTitleWorkItemCollection")).then(() => {

                this.TabPage.Title(Bao.Session.GetMessage("msgTitleWorkItemCollection")());
                this.ColHeaderNumber = Bao.Session.GetMessage("msgGridColNumber");
                this.ColHeaderTitle = Bao.Session.GetMessage("msgGridColTitle");
                this.ColHeaderDescription = Bao.Session.GetMessage("msgGridColDescription");
                this.ColHeaderPriority = Bao.Session.GetMessage("msgGridColPriority");
                this.ColHeaderCategory = Bao.Session.GetMessage("msgGridColCategory");
                this.ColHeaderStartDate = Bao.Session.GetMessage("msgGridColStartDate");
                this.ColHeaderEndDate = Bao.Session.GetMessage("msgGridColEndDate");
                this.ColHeaderWorkItemTypeName = Bao.Session.GetMessage("msgGridColType");
                this.ColHeaderWorkItemStatus = Bao.Session.GetMessage("msgGridColState");

                def.resolve(null);
            }).always(_ => {
                this.TabPage.IsLoading(false);
            });

        return def.promise();
    }

    /**
     * Set DataGrid Columns
     */
    GetAvailableColumns = () => {
        let availableColumns = [
            {
                "PropertyName": "Icon",
                "Sort": 2,
                "HeaderText": "",
                "Width": 50,
                "ColumnType": "iconClass",
            },
            {
                "PropertyName": "KeyNumber",
                "Sort": 3,
                "HeaderText": this.ColHeaderNumber()
            },
            {
                "PropertyName": "Title",
                "Sort": 4,
                "HeaderText": this.ColHeaderTitle(),
                "Width": 100,
                "ColumnType": "editlink"
            },
            {
                "PropertyName": "Description",
                "Sort": 5,
                "HeaderText": this.ColHeaderDescription(),
            },
            {
                "PropertyName": "Priority",
                "Sort": 6,
                "HeaderText": this.ColHeaderPriority(),
            },
            {
                "PropertyName": "Category",
                "Sort": 7,
                "HeaderText": this.ColHeaderCategory(),
            },
            {
                "PropertyName": "StartDateTimezoneCorrected",
                "Sort": 8,
                "HeaderText": this.ColHeaderStartDate(),
                "ColumnType": "datetime",
                "AllowFiltering": false
            },
            {
                "PropertyName": "EndDateTimezoneCorrected",
                "Sort": 9,
                "HeaderText": this.ColHeaderEndDate(),
                "ColumnType": "datetime",
                "AllowFiltering": false
            },
            {
                "PropertyName": "WorkItemTypeName",
                "Sort": 10,
                "HeaderText": this.ColHeaderWorkItemTypeName()
            },
            {
                "PropertyName": "Status",
                "Sort": 11,
                "HeaderText": this.ColHeaderWorkItemStatus()
            }
        ];
        this.AvailableColumns(availableColumns);
    };

    /**
     * Get all of the rows for the DataGrid depending on the supplied parameters
     * @param headerFilters - Filters used in the request URL (see {@link https://learn.microsoft.com/en-us/odata/concepts/queryoptions-overview})
     * @param skip - The number of rows to skip
     * @param take - The number of rows to take
     * @param sortingParam - The columns to sort by and the direction to sort
     * @param groupingParam - The columns to group by
     */
    loadTableControlData = (headerFilters, skip, take, sortingParam, groupingParam) => {
        let def = $.Deferred<any>();
        let customFilters: Models.HashObject[] = this.GetCustomFilter();

        let url = "Search/SearchItems";

        if (headerFilters?.length > 0) {
            url += "?$filter=" + headerFilters;
            url = encodeURI(url);
        }

        Bao.post(url, JSON.stringify({
            searchKey: this.SearchKey === undefined ? "" : this.SearchKey(),
            searchString: "",
            customFilter: customFilters,
            sorting: sortingParam,
            skip: skip,
            take: take
        }), function (searchResult) {
            if (searchResult != null) {
                def.resolve({
                    data: searchResult.Models,
                    totalCount: searchResult.TotalRows
                });
            }
            else {
                def.resolve(null);
            }
        }).always(_ => {
            this.TabPage.IsLoading(false);
        });
        return def.promise();
    }

    /**
     * Gets the EdmModel for the DataGrid (Each DataType of the WorkItemModel-Properties)
    */
    loadModelMetadata = () => {
        return Bao.post("Search/GetModelMetadata", JSON.stringify({
            searchKey: ko.unwrap(this.SearchKey)
        })).then(result => {
            return result?.EdmModel;
        });
    }

    /** 
     * Handle buttons from the toolbar
     */
    RegisterMenuClick = function () {
        BaoMenu.registerEvent((btn) => {
            this.ToolbarMenuClickHandler(btn.ActionKey())
        });
    };
    /***
     * Handler the click events according to the actionkey 
     * */
    ToolbarMenuClickHandler = (actionKey) => {
        if (this.TabPage != null && this.TabPage.IsActiveTab != null && this.TabPage.IsActiveTab() === true) {
            switch (actionKey) {
                case "WorkItemCollectionNew":
                    this.CreateNewWorkItem();
                    break;
                case "WorkItemCollectionRefresh":
                    this.RefreshList();
                    break;
            }
        }
    }
    /***
     * Create the new work item
     * */
    CreateNewWorkItem = () => {
        let linkHash = 'WorkItem/' + Guid() + '/null/' + this.WorkItemCollectionSelectedId();
        baoRouter.setHash(linkHash);
    };

    //#endregion

    //#region Protected Methods

    /**
     * Get WorkItemCollections
     * @returns
     */
    protected loadWorkItemCollections = () => {
        this.TabPage.IsLoading(true);
        return Bao.post("WorkItemCollection/GetWorkItemCollections", JSON.stringify({ addEmptyRecord: true }))
            .done(result => {
                this.WorkItemCollections(result.CollectionsForCurrentUser);
                this.WorkItemCollectionsPermissions = result.Permissions;

                let workItemCollectionId = baoRouter.getRouteValue("WorkItemCollectionId", this.TabPage);
                if (workItemCollectionId && this.WorkItemCollections().filter(t => t.WorkItemCollectionId === workItemCollectionId).length > 0) {
                    this.WorkItemCollectionId(workItemCollectionId);
                }

            }).always(_ => {
                this.TabPage.IsLoading(false);
            });

    }
    
    /**
     * Refreshes the list
     * @returns
     */
    protected RefreshList = () => {
        if (!this.IsLoaded || this.ChangingData || !this.WorkItemCollectionId() ) {
            return;
        }

        Bao.raiseEvent("table-control:load", this.SearchKey);
    }

    /**
    * Sets custom filters
    * @returns array of filters
    */
    protected GetCustomFilter(): Models.HashObject[] {
        let customFilterHashObject: Models.HashObject[] = [];
        customFilterHashObject.push({ 'Key': 'WorkItemCollectionId', 'Value': this.WorkItemCollectionId() });

        if (this.StartDateHtml()) {
            customFilterHashObject.push({
                Key: 'FromDate',
                Value: this.StartDateHtml()
            });
        }

        if (this.EndDateHtml()) {
            customFilterHashObject.push({
                Key: 'ToDate',
                Value: this.EndDateHtml()
            });
        }

        if (this.StartDateHtml() || this.EndDateHtml()) {
            customFilterHashObject.push({
                Key: 'DateFilterType',
                Value: 'ValidFrom'
            });
        }

        customFilterHashObject.push({
            Key: 'ShowVirtual',
            Value: this.ShowVirtual()
        });

        return customFilterHashObject;
    }

    /**
     * Get the permissions object for the current selected WorkItemCollection
     * */
    protected GetCurrentBooksPermissions = () => {
        let permissions: any = null;
        if (this.WorkItemCollectionsPermissions) {
            if (this.WorkItemCollectionId()) {
                permissions = this.WorkItemCollectionsPermissions[this.WorkItemCollectionId()];
            }
            if (!permissions) {
                permissions = this.WorkItemCollectionsPermissions['00000000-0000-0000-0000-000000000000'];
            }
            return permissions;
        }

        return null;
    }
    /**
     * Shows/Hides toolbar buttons for the current BriefingBook permissions (RoleShiftbooks)
     * */
    protected SetPermissions = () => {
        let permissions = this.GetCurrentBooksPermissions();
        if (!permissions) {
            return;
        }
        for (let actionKey in permissions) {
            BaoMenu.permissionVisible({ ActionKey: actionKey, Context: this.TabPage.Context(), Visible: permissions[actionKey] });
        }
    }

    //#endregion
}
export default WorkItemCollection;